import type { FC, JSX } from "react";
import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import type { ILegendPropsWithValue } from "@similarweb/ui-components";
import { FlexColumn, LegendWithOneLineCheckboxFlex, SearchInput } from "@similarweb/ui-components";

import { Legends as LegendsView } from "app/components/React/Legends/Legends";
import type { ILegendItem, ILegendItems } from "app/components/React/Legends/legendsTypes";
import { translate } from "app/filters/i18nFilter";
import type { LengthArray } from "app/UtilitiesAndConstants/types/lengthArray";
import { FadeWrapper } from "app/UtilitiesAndConstants/UtilitiesComponents/FadeWrapper";

import {
    LegendItemsContainer,
    OtherLegendsContainer,
    SearchInputContainer,
    SelectedAttributes,
    Title,
    TitlesContainer,
    StyledLegendWrapper,
    HeaderContainer,
} from "./styled";
interface IText {
    searchPlaceHolder: string;
    searchLabel: string;
}

interface ILegendsProps {
    legendItems: ILegendItems;
    toggleSeries: (legendItem: ILegendItem, hidden?: boolean, event?: MouseEvent) => void;
    legendComponent?(props: ILegendPropsWithValue): JSX.Element;
    textMaxWidth?: string;
    subtitleMaxWidth?: string;
}

interface IVerticalLegendsWithSearch extends ILegendsProps {
    maxSelection: number;
    otherLegend?: ILegendItem;
    legendItemsHeight: string;
    text: IText;
    titles?: LengthArray<string, 2>;
    limitationReachedTooltip?: string;
    legendsFadeHeight?: string;
    hasSearch?: boolean;
}

const DEFAULT_DISABLED_COLOR = colorsPalettes.carbon[50];

export const Legends = ({
    legendItems,
    toggleSeries,
    legendComponent = LegendWithOneLineCheckboxFlex,
    textMaxWidth = "130px",
    subtitleMaxWidth = "130px",
}: ILegendsProps) => {
    return (
        <LegendsView
            legendItems={legendItems}
            toggleSeries={toggleSeries}
            legendComponent={legendComponent}
            legendComponentWrapper={StyledLegendWrapper}
            gridDirection="column"
            textMaxWidth={textMaxWidth}
            subtitleMaxWidth={subtitleMaxWidth}
        />
    );
};

export const VerticalLegendsWithSearch: FC<IVerticalLegendsWithSearch> = (props) => {
    const {
        legendItems,
        maxSelection,
        otherLegend,
        toggleSeries,
        legendItemsHeight,
        text,
        titles,
        legendComponent = LegendWithOneLineCheckboxFlex,
        textMaxWidth = "130px",
        subtitleMaxWidth = "130px",
        limitationReachedTooltip = "checkbox.max.selection",
        legendsFadeHeight,
        hasSearch = true,
    } = props;
    const { searchPlaceHolder, searchLabel } = text;
    const [query, setQuery] = useState<string>();
    const displayLegends = useMemo(() => {
        const displayLegends = query
            ? legendItems.filter(({ name }) => {
                  if (typeof name !== "string") {
                      return name; // if the legendItem is an element the query search wont work
                  }
                  return name.toLowerCase().includes(query.toLowerCase());
              })
            : legendItems;
        return displayLegends;
    }, [query, legendItems]);
    const onSearch = (value) => setQuery(value);

    const visibleFilter = ({ visible }) => visible;

    const selectedLegendsQueryAgnostic = legendItems.filter(visibleFilter);

    const enrichedDisplayLegends = displayLegends.map((legendItem) => {
        const isLimitationReached = !legendItem.visible && selectedLegendsQueryAgnostic.length === maxSelection;

        return {
            ...legendItem,
            isDisabled: isLimitationReached || legendItem.isDisabled,
            ...(isLimitationReached && {
                tooltip: translate(limitationReachedTooltip),
                color: DEFAULT_DISABLED_COLOR,
            }),
        };
    });
    const enrichedOtherDisplayLegend = { ...otherLegend, isDisabled: otherLegend?.isDisabled ?? false };
    return (
        <>
            <HeaderContainer>
                {hasSearch ? (
                    <FlexColumn>
                        <SearchInputContainer>
                            <SearchInput
                                defaultValue={query}
                                onChange={onSearch}
                                debounce={400}
                                placeholder={searchPlaceHolder}
                            />
                        </SearchInputContainer>
                        <SelectedAttributes>{`${selectedLegendsQueryAgnostic.length}/${maxSelection} ${searchLabel}`}</SelectedAttributes>
                    </FlexColumn>
                ) : null}
                {titles ? (
                    <TitlesContainer>
                        {titles.map((title, index) => (
                            <Title key={index}>{title}</Title>
                        ))}
                    </TitlesContainer>
                ) : null}
            </HeaderContainer>
            <LegendItemsContainer height={legendItemsHeight}>
                <FadeWrapper fadeHeight={legendsFadeHeight}>
                    <Legends
                        legendItems={enrichedDisplayLegends}
                        toggleSeries={toggleSeries}
                        legendComponent={legendComponent}
                        textMaxWidth={textMaxWidth}
                        subtitleMaxWidth={subtitleMaxWidth}
                    />
                </FadeWrapper>
            </LegendItemsContainer>
            {otherLegend ? (
                <OtherLegendsContainer>
                    <Legends
                        legendItems={[enrichedOtherDisplayLegend]}
                        toggleSeries={toggleSeries}
                        legendComponent={legendComponent}
                        textMaxWidth={textMaxWidth}
                    />
                </OtherLegendsContainer>
            ) : null}
        </>
    );
};
